import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { CORRELATION_ID_HEADER, isHeaderSkippedByPolicy } from '../api/interceptors/header-injection.context';
import { FHIR_API_BASE_URL } from './api-client.tokens';

export { CORRELATION_ID_HEADER };

export const SESSION_CORRELATION_ID_KEY = 'symphony.session.correlationId';

function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
}

/**
 * Resolves a single correlation ID for the lifetime of the browser tab session, generating and persisting one in
 * sessionStorage on first use so every request in the session shares the same ID (enables end-to-end tracking of a
 * user's session across backend services).
 */
function getSessionCorrelationId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_CORRELATION_ID_KEY);
    if (existing) {
      return existing;
    }

    const generated = generateCorrelationId();
    sessionStorage.setItem(SESSION_CORRELATION_ID_KEY, generated);
    return generated;
  } catch {
    // sessionStorage unavailable (e.g. privacy mode) — fall back to a per-request ID.
    return generateCorrelationId();
  }
}

function isFhirRequest(url: string, baseUrl: string | null | undefined): boolean {
  if (!baseUrl) {
    return false;
  }

  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const pathPart = url.replace(/^https?:\/\/[^/]+/, '') || url;

  return (
    pathPart === normalizedBase ||
    pathPart.startsWith(`${normalizedBase}/`) ||
    url === normalizedBase ||
    url.startsWith(`${normalizedBase}/`)
  );
}

/**
 * CorrelationInterceptor
 *
 * Centralized functional HTTP interceptor that automatically attaches an `X-Correlation-ID` header to all outgoing API
 * requests (excluding standard FHIR endpoints and requests configured with skip header policy).
 *
 * The same correlation ID is reused for every request within the browser tab session (persisted in sessionStorage) so
 * that end-to-end tracing can follow a single user's session across web applications and backend APIs, rather than
 * generating a new ID per request.
 */
export const correlationInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  // If the header is already explicitly set (case-insensitive check), pass through unchanged
  if (req.headers.has(CORRELATION_ID_HEADER) || req.headers.has('x-correlation-id')) {
    return next(req);
  }

  // Check if header is skipped via HttpContext policy
  if (isHeaderSkippedByPolicy(req.context, CORRELATION_ID_HEADER)) {
    return next(req);
  }

  // FHIR endpoints are standard HL7 FHIR APIs and should not receive non-standard application headers
  const fhirBaseUrl = inject(FHIR_API_BASE_URL, { optional: true });
  if (isFhirRequest(req.url, fhirBaseUrl)) {
    return next(req);
  }

  const correlationId = getSessionCorrelationId();
  const clonedReq = req.clone({
    setHeaders: {
      [CORRELATION_ID_HEADER]: correlationId,
    },
  });

  return next(clonedReq);
};
